import CustomButton from "@components/buttons/CustomButton";
import { CurrencyLogoDropdown } from "@components/dropdowns/CurrencyLogoDropdown";
import CheckBox from "@components/inputs/CheckBox";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { invoiceServices } from "@services/apis/Invoice";
import { transactionApiServices } from "@services/apis/Transactions";
import { GetExchangesData } from "@services/models/InvoiceApprovals/GetExchangesModel";
import { UpdateExchangeRequest } from "@services/models/InvoiceApprovals/UpdateExchangeModel";
import { GetTokenListData } from "@services/models/Transactions/GetTokenList";
import { useEffect, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface EditExchangeSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onExchangeUpdated?: () => void;
  exchangeData?: GetExchangesData | null;
}

const EditExchangeSlider: React.FC<EditExchangeSliderProps> = ({
  isOpen,
  onClose,
  onExchangeUpdated,
  exchangeData,
}) => {
  const [exchangeName, setExchangeName] = useState<string>("");
  const [cryptoAddress, setCryptoAddress] = useState<string>("");
  const [selectedToken, setSelectedToken] = useState<string>("");
  const [selectedNetwork, setSelectedNetwork] = useState<string>("");
  const [tokenId, setTokenId] = useState<number | undefined>();
  const [networkId, setNetworkId] = useState<number | undefined>();
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [tokenList, setTokenList] = useState<GetTokenListData[]>([]);
  const [tokenListLoading, setTokenListLoading] = useState<boolean>(false);

  const { handleError, handleSuccess } = useSnackbarMessage();

  const fetchTokenList = async () => {
    try {
      setTokenListLoading(true);
      const response = await transactionApiServices.getTokenList();
      setTokenList(response.data);
    } catch (error) {
      handleError({ message: "Failed to load token list" });
    } finally {
      setTokenListLoading(false);
    }
  };

  const handleTokenSelection = (value: string | number) => {
    const currencyId = Number(value);
    const token = tokenList.find((t) => t.currencyId === currencyId);

    if (token) {
      setTokenId(currencyId);
      setSelectedToken(token.currencySymbol);
      setNetworkId(undefined);
      setSelectedNetwork("");
    }
  };

  const handleNetworkSelection = (value: string | number) => {
    const networkId = Number(value);
    const token = tokenList.find((t) => t.currencyId === tokenId);
    const network = token?.networks?.find((n) => n.networkId === networkId);

    if (network) {
      setNetworkId(networkId);
      setSelectedNetwork(network.networkSymbol);
    }
  };

  const handleSubmit = async () => {
    if (!exchangeData?.exchangeId) {
      handleError({ message: "Exchange ID is required" });
      return;
    }
    if (!exchangeName.trim()) {
      handleError({ message: "Please enter exchange name" });
      return;
    }
    if (!selectedToken) {
      handleError({ message: "Please select a token" });
      return;
    }
    if (!selectedNetwork) {
      handleError({ message: "Please select a network" });
      return;
    }
    if (!cryptoAddress.trim()) {
      handleError({ message: "Please enter crypto address" });
      return;
    }

    try {
      setIsLoading(true);
      const request: UpdateExchangeRequest = {
        exchangeId: exchangeData.exchangeId,
        exchange: exchangeName.trim(),
        token: selectedToken,
        network: selectedNetwork,
        address: cryptoAddress.trim(),
        isActive: isActive,
      };

      await invoiceServices.updateExchange(request);

      handleSuccess({ message: "Exchange updated successfully" });

      if (onExchangeUpdated) {
        onExchangeUpdated();
      }

      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg || "Failed to update exchange";
      handleError({ message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setExchangeName("");
    setCryptoAddress("");
    setSelectedToken("");
    setSelectedNetwork("");
    setTokenId(undefined);
    setNetworkId(undefined);
    setIsActive(true);
  };

  // Populate form when exchange data changes
  useEffect(() => {
    if (exchangeData && isOpen) {
      setExchangeName(exchangeData.exchange || "");
      setCryptoAddress(exchangeData.address || "");
      setSelectedToken(exchangeData.token || "");
      setSelectedNetwork(exchangeData.network || "");
      setIsActive(Boolean(exchangeData.isActive));

      // Find and set the token and network IDs from the token list
      if (tokenList.length > 0) {
        const token = tokenList.find(
          (t) => t.currencySymbol === exchangeData.token
        );
        if (token) {
          setTokenId(token.currencyId);
          const network = token.networks?.find(
            (n) => n.networkSymbol === exchangeData.network
          );
          if (network) {
            setNetworkId(network.networkId);
          }
        }
      }
    } else if (!isOpen) {
      resetForm();
    }
  }, [exchangeData, isOpen, tokenList]);

  useEffect(() => {
    if (isOpen) {
      fetchTokenList();
    }
  }, [isOpen]);

  const isFormValid = Boolean(
    exchangeName.trim() &&
      selectedToken &&
      selectedNetwork &&
      cryptoAddress.trim()
  );

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading={"Edit Exchange"}
      onClose={() => {
        resetForm();
        onClose();
      }}
      onClickCross={() => {
        resetForm();
        onClose();
      }}
      showConfirm={false}
      isSlider={true}
      widthDesktop={"35%"}
    >
      <div className="flex flex-col px-[16px] py-[16px] mt-[60px]">
        <div className="flex flex-col gap-4 bg-bg-base rounded-lg p-4 border border-border-subtle">
          <GlobalTextInputField
            placeholder="Enter exchange name"
            value={exchangeName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setExchangeName(e.target.value)
            }
            title="Exchange Name"
          />

          {!tokenListLoading && tokenList.length > 0 && (
            <CurrencyLogoDropdown
              listOfValues={tokenList.map((token) => ({
                id: token.currencyId,
                currency: token.currencySymbol,
                currencyLogo: token.currencyImg,
                currencyName: token.currencyName || token.currencySymbol,
                isToken: true,
                isReceive: true,
                key: token.currencyId,
              }))}
              value={tokenId}
              onChange={handleTokenSelection}
              label="Select Token"
              color="bg-main-container-header"
              baseColor="bg-bg-page-body"
              borderColor="border-subtle"
              expandedBorderColor="border-button-primary"
            />
          )}

          <CurrencyLogoDropdown
            listOfValues={
              tokenList
                ?.find((t) => t.currencyId === tokenId)
                ?.networks?.map((network) => ({
                  id: network.networkId,
                  currency: network.networkSymbol,
                  currencyLogo: network.networkImg,
                  isToken: false,
                  standard: network.standard,
                  networkName: network.networkName || network.networkSymbol,
                  isReceive: true,
                  isReceiveNetwork: true,
                  key: network.networkId,
                })) || []
            }
            value={networkId}
            onChange={handleNetworkSelection}
            label="Select Network"
            color="bg-main-container-header"
            baseColor="bg-bg-page-body"
            borderColor="border-subtle"
            expandedBorderColor="border-button-primary"
          />

          <GlobalTextInputField
            placeholder="Enter crypto address"
            value={cryptoAddress}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCryptoAddress(e.target.value)
            }
            title="Crypto Address"
          />

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-text-base">Status</label>
            <div className="flex items-center gap-2">
              <CheckBox
                checked={isActive}
                onChange={() => setIsActive(!isActive)}
              />
              <span className="text-sm text-text-base">Active</span>
            </div>
          </div>
          <CustomButton
            text="UPDATE EXCHANGE"
            onClick={handleSubmit}
            isActive={isFormValid && !isLoading}
            isButtonLoading={isLoading}
            className="flex-1 w-full"
          />
        </div>
      </div>
    </SlidingPanel>
  );
};

export default EditExchangeSlider;
